/**
 * Item Master — enhanced with:
 *   - Dynamic category dropdown (+ inline Add)
 *   - Category-aware fields (Cotton Specs / Spares ID / Pack specs)
 *   - 4 PDF uploads (Catalog / Drawing / Manual / Spec)
 *   - Approved Vendors sub-panel
 */
import { useState, useMemo, useEffect } from 'react'
import {
  Button, Form, Input, InputNumber, Select, Switch, Tag, Space, App, Divider,
  Modal, Tabs, Upload, Popconfirm, Table,
} from 'antd'
import {
  PlusOutlined, EditOutlined, UploadOutlined, DownloadOutlined,
  StarFilled, DeleteOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { itemsApi, suppliersApi } from '@/features/gate/api'
import {
  itemCategoriesApi, itemVendorsApi, itemDocumentsApi,
} from '@/features/categories/api'
import type { ItemMaster } from '@/shared/types/master'
import type { ItemVendor } from '@/shared/types/categories'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const UOM_LIST = ['KG', 'MT', 'NOS', 'MTR', 'LTR', 'BOX', 'PKT', 'BALE', 'ROLL']

const DOC_TYPES = [
  { key: 'catalog',  label: 'Vendor Catalog' },
  { key: 'drawing',  label: 'Engineering Drawing' },
  { key: 'manual',   label: 'Operation Manual' },
  { key: 'spec',     label: 'Spec Sheet' },
]

export default function ItemsPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<ItemMaster | null>(null)
  const [activeTab, setActiveTab] = useState('basic')
  const [catModalOpen, setCatModalOpen] = useState(false)
  const [catForm] = Form.useForm()

  const canCreate = usePermission('item.create')
  const canUpdate = usePermission('item.update')

  const { data, isLoading } = useQuery({
    queryKey: ['items', page, pageSize, search, categoryFilter],
    queryFn: () => itemsApi.list({ page, size: pageSize, search: search || undefined, item_category: categoryFilter }),
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['item-categories'],
    queryFn: () => itemCategoriesApi.list(true),
  })

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: () => suppliersApi.list({ size: 500, status: 'ACTIVE' }),
    select: (d: any) => d.items ?? [],
  })

  // Watch selected item_category → determine sub_type for UI
  const selectedCategoryCode = Form.useWatch('item_category', form)
  const selectedSubType = useMemo(() => {
    const cat = categories.find(c => c.code === selectedCategoryCode)
    return cat?.sub_type ?? 'OTHER'
  }, [selectedCategoryCode, categories])

  const openCreate = () => {
    setEditing(null)
    setActiveTab('basic')
    form.resetFields()
    const defaultCat = categories.find(c => c.code === 'COTTON_RAW') ?? categories[0]
    form.setFieldsValue({
      is_active: true,
      item_category: defaultCat?.code ?? 'OTHER',
      uom: 'KG',
      gst_rate_pct: 5,
      standard_moisture_pct: 8.5,
    })
    setDrawerOpen(true)
  }

  const openEdit = (record: ItemMaster) => {
    setEditing(record)
    setActiveTab('basic')
    form.setFieldsValue(record)
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editing ? itemsApi.update(editing.id, values) : itemsApi.create(values as never),
    onSuccess: (it: ItemMaster) => {
      qc.invalidateQueries({ queryKey: ['items'] })
      message.success(editing ? 'Item updated' : 'Item created — now upload documents')
      setEditing(it)  // keep drawer open so user can upload PDFs
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  const addCategoryMutation = useMutation({
    mutationFn: (v: any) => itemCategoriesApi.create(v),
    onSuccess: (cat) => {
      qc.invalidateQueries({ queryKey: ['item-categories'] })
      message.success(`Category "${cat.name}" added`)
      form.setFieldValue('item_category', cat.code)
      setCatModalOpen(false)
      catForm.resetFields()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    { title: 'Code', dataIndex: 'item_code', key: 'code', width: 130 },
    { title: 'Name', dataIndex: 'item_name', key: 'name' },
    {
      title: 'Category', dataIndex: 'item_category', key: 'cat',
      render: (v: string) => {
        const cat = categories.find(c => c.code === v)
        return <Tag>{cat?.name ?? v.replace(/_/g, ' ')}</Tag>
      },
    },
    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 70 },
    {
      title: 'Make / Model', key: 'make',
      render: (_: unknown, r: ItemMaster) => (r as any).make || (r as any).model_no
        ? <span style={{ fontSize: 12 }}>{(r as any).make ?? ''} {(r as any).model_no ?? ''}</span>
        : '—',
    },
    {
      title: 'Part / Catalog', key: 'part',
      render: (_: unknown, r: ItemMaster) => {
        const parts = []
        if ((r as any).part_no) parts.push(`P/N ${(r as any).part_no}`)
        if ((r as any).catalog_no) parts.push(`Cat ${(r as any).catalog_no}`)
        return parts.length ? <span style={{ fontSize: 11, fontFamily: 'monospace' }}>{parts.join(' · ')}</span> : '—'
      },
    },
    {
      title: 'Criticality', dataIndex: 'criticality', key: 'crit',
      render: (v?: string) => v
        ? <Tag color={v === 'A' ? 'red' : v === 'B' ? 'orange' : 'default'}>{v}</Tag>
        : '—',
    },
    {
      title: 'Status', dataIndex: 'is_active', key: 'status',
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, record: ItemMaster) =>
        canUpdate ? <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Edit</Button> : null,
    },
  ]

  // ── Tab: Documents ─────────────────────────────────────────────────────────
  const renderDocUploadRow = (doc: { key: string; label: string }) => {
    if (!editing) return null
    const urlField = `docs_${doc.key}_url` as keyof ItemMaster
    const uploaded = (editing as any)[urlField] as string | undefined
    return (
      <div key={doc.key} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 0', borderBottom: '1px solid #f0f0f0',
      }}>
        <Space>
          <span>{doc.label}</span>
          {uploaded && <Tag color="green" style={{ fontSize: 10 }}>Uploaded</Tag>}
        </Space>
        <Space>
          {uploaded && (
            <Button size="small" icon={<DownloadOutlined />}
              onClick={() => window.open(itemDocumentsApi.getUrl(editing.id, doc.key), '_blank')}>
              View PDF
            </Button>
          )}
          <Upload
            maxCount={1}
            showUploadList={false}
            accept=".pdf,.jpg,.jpeg,.png"
            beforeUpload={(file) => {
              itemDocumentsApi.upload(editing.id, doc.key, file).then((updated) => {
                message.success('Uploaded')
                setEditing(updated)
                qc.invalidateQueries({ queryKey: ['items'] })
              }).catch((e) => message.error(e?.response?.data?.detail ?? 'Upload failed'))
              return false
            }}
          >
            <Button size="small" icon={<UploadOutlined />}>{uploaded ? 'Replace' : 'Upload'}</Button>
          </Upload>
        </Space>
      </div>
    )
  }

  // ── Tab: Approved Vendors ───────────────────────────────────────────────────
  const VendorsTab = () => {
    const [vform] = Form.useForm()
    const [vopen, setVOpen] = useState(false)

    const { data: links = [], refetch } = useQuery({
      queryKey: ['item-vendors', editing?.id],
      queryFn: () => itemVendorsApi.list(editing!.id),
      enabled: !!editing?.id,
    })

    const addM = useMutation({
      mutationFn: (v: any) => itemVendorsApi.add(editing!.id, v),
      onSuccess: () => { refetch(); setVOpen(false); vform.resetFields(); message.success('Vendor added') },
      onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
    })
    const togglePrimary = useMutation({
      mutationFn: (iv: ItemVendor) => itemVendorsApi.update(iv.id, { is_primary: !iv.is_primary }),
      onSuccess: () => refetch(),
    })
    const removeM = useMutation({
      mutationFn: (id: string) => itemVendorsApi.remove(id),
      onSuccess: () => { refetch(); message.success('Removed') },
    })

    return (
      <div>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setVOpen(true)}
          style={{ marginBottom: 12 }}>
          Add Approved Vendor
        </Button>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={links}
          columns={[
            {
              title: 'Primary', dataIndex: 'is_primary', width: 80,
              render: (v: boolean, r: ItemVendor) => (
                <Button
                  type="text" size="small"
                  icon={<StarFilled style={{ color: v ? '#faad14' : '#d9d9d9' }} />}
                  onClick={() => togglePrimary.mutate(r)}
                />
              ),
            },
            { title: 'Vendor', render: (r: ItemVendor) => (
              <Space direction="vertical" size={0}>
                <span>{r.supplier_name}</span>
                <span style={{ fontSize: 10, color: '#888' }}>{r.supplier_code}</span>
              </Space>
            ) },
            { title: 'Vendor Part No.', dataIndex: 'supplier_part_no', render: (v?: string) => v ?? '—' },
            { title: 'Last Rate', dataIndex: 'last_rate',
              render: (v?: string) => v ? `₹${Number(v).toFixed(2)}` : '—' },
            { title: 'Remarks', dataIndex: 'remarks', render: (v?: string) => v ?? '—' },
            { title: '', key: 'actions', width: 60,
              render: (_: unknown, r: ItemVendor) => (
                <Popconfirm title="Remove this vendor link?" onConfirm={() => removeM.mutate(r.id)}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              ),
            },
          ]}
        />

        <Modal
          title="Add Approved Vendor"
          open={vopen}
          onCancel={() => setVOpen(false)}
          onOk={() => vform.validateFields().then(v => addM.mutate(v))}
          confirmLoading={addM.isPending}
        >
          <Form form={vform} layout="vertical" style={{ marginTop: 12 }}>
            <Form.Item name="supplier_id" label="Vendor" rules={[{ required: true }]}>
              <Select
                showSearch optionFilterProp="label"
                options={(suppliers as any[]).map(s => ({ value: s.id, label: `${s.supplier_code} — ${s.supplier_name}` }))}
              />
            </Form.Item>
            <Form.Item name="supplier_part_no" label="Vendor Part No."><Input /></Form.Item>
            <Form.Item name="last_rate" label="Last Rate (₹)">
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
            </Form.Item>
            <Form.Item name="is_primary" label="Primary Vendor" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
          </Form>
        </Modal>
      </div>
    )
  }

  // ── Tabs ────────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'basic', label: 'Basic',
      children: (
        <>
          <Form.Item name="item_name" label="Item Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="item_category" label="Category" rules={[{ required: true }]}>
            <Select
              showSearch optionFilterProp="label"
              options={categories.map(c => ({ value: c.code, label: c.name }))}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '4px 0' }} />
                  <Button type="link" icon={<PlusOutlined />} onClick={() => setCatModalOpen(true)}
                    style={{ width: '100%', textAlign: 'left' }}>
                    Add new category
                  </Button>
                </>
              )}
            />
          </Form.Item>
          <Form.Item name="uom" label="UOM" rules={[{ required: true }]}>
            <Select options={UOM_LIST.map(u => ({ value: u, label: u }))} />
          </Form.Item>
          <Form.Item name="hsn_code" label="HSN Code"><Input maxLength={8} /></Form.Item>
          <Form.Item name="gst_rate_pct" label="GST Rate (%)">
            <InputNumber style={{ width: '100%' }} min={0} max={28} step={0.01} />
          </Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item>
        </>
      ),
    },

    // Show Identification tab only for SPARES
    ...(selectedSubType === 'SPARES' ? [{
      key: 'ident', label: 'Identification',
      children: (
        <>
          <Form.Item name="catalog_no" label="Catalog No.">
            <Input style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <Form.Item name="drawing_no" label="Drawing No.">
            <Input style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <Form.Item name="part_no" label="OEM Part No.">
            <Input style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <Form.Item name="make" label="Make / Manufacturer"><Input /></Form.Item>
          <Form.Item name="model_no" label="Model No."><Input /></Form.Item>
          <Form.Item name="equipment_ref" label="Equipment / Machine Reference"
            help="e.g. 'Ring Frame LR6 Model 2022' or 'Autoconer 21C'">
            <Input />
          </Form.Item>
          <Form.Item name="specifications" label="Technical Specifications"
            help="Dimensions / tolerances / ratings / any other specs">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="criticality" label="Criticality"
            help="A = Mission critical (stops production), B = Important, C = Non-critical">
            <Select options={[
              { value: 'A', label: 'A — Mission Critical' },
              { value: 'B', label: 'B — Important' },
              { value: 'C', label: 'C — Non-critical' },
            ]} />
          </Form.Item>
        </>
      ),
    }] : []),

    // Cotton specs tab — only for RAW_COTTON
    ...(selectedSubType === 'RAW_COTTON' ? [{
      key: 'cotton', label: 'Cotton Specs',
      children: (
        <>
          <Form.Item name="cotton_variety" label="Variety" help="e.g. Shankar-6, Suvin, MCU-5"><Input /></Form.Item>
          <Form.Item name="cotton_origin" label="Origin" help="e.g. Gujarat, Telangana"><Input /></Form.Item>
          <Form.Item name="cotton_grade" label="Grade" help="e.g. Shankar-6 Medium Staple"><Input /></Form.Item>
          <Form.Item name="staple_length_mm" label="Staple Length (mm)">
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
          <Form.Item name="micronaire" label="Micronaire">
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
          <Form.Item name="standard_moisture_pct" label="Standard Moisture %">
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
        </>
      ),
    }] : []),

    // Pack specs tab — for CHEMICAL/PACKING/CONSUMABLE
    ...(['CHEMICAL', 'PACKING', 'CONSUMABLE'].includes(selectedSubType) ? [{
      key: 'pack', label: 'Pack Specs',
      children: (
        <>
          <Form.Item name="pack_unit" label="Pack Unit" help="e.g. DRUM, BAG, BOX">
            <Select options={['DRUM', 'BAG', 'BOX', 'BOTTLE', 'CARTON', 'ROLL', 'CAN', 'PIECE'].map(v => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="pack_size" label="Pack Size" help="e.g. 200 (kg/L/pieces per pack)">
            <InputNumber style={{ width: '100%' }} min={0} step={0.001} />
          </Form.Item>
          {selectedSubType === 'CHEMICAL' && (
            <Form.Item name="concentration_pct" label="Concentration %">
              <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} />
            </Form.Item>
          )}
          <Form.Item name="shelf_life_months" label="Shelf Life (months)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </>
      ),
    }] : []),

    {
      key: 'commercial', label: 'Commercial',
      children: (
        <>
          <Form.Item name="preferred_supplier_id" label="Preferred Supplier">
            <Select
              showSearch allowClear optionFilterProp="label"
              options={(suppliers as any[]).map(s => ({
                value: s.id,
                label: `${s.supplier_code} — ${s.supplier_name}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="lead_time_days" label="Lead Time (days)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="last_purchase_rate" label="Last Purchase Rate (₹)">
            <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
          </Form.Item>
          <Form.Item name="min_stock_level" label="Minimum Stock Level">
            <InputNumber style={{ width: '100%' }} min={0} step={0.001} />
          </Form.Item>
          <Form.Item name="reorder_level" label="Reorder Level">
            <InputNumber style={{ width: '100%' }} min={0} step={0.001} />
          </Form.Item>
        </>
      ),
    },

    ...(editing ? [{
      key: 'docs', label: 'Documents',
      children: (
        <div>
          <p style={{ color: '#888', fontSize: 12 }}>
            Upload PDFs from the machine supplier. Max 10 MB each.
          </p>
          {DOC_TYPES.map(renderDocUploadRow)}
        </div>
      ),
    }] : []),

    ...(editing ? [{
      key: 'vendors', label: 'Approved Vendors',
      children: <VendorsTab />,
    }] : []),
  ]

  return (
    <div>
      <PageHeader
        title="Item Master"
        subtitle="Raw Material, Spares, Consumables, Packing, Chemicals, Yarn — with PDFs, specs, and approved vendors"
        extra={canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Item</Button>
        )}
      />

      <Space style={{ marginBottom: 16 }}>
        <Input.Search placeholder="Search code, name, variety, part no…" allowClear
          onSearch={(v) => { setSearch(v); setPage(1) }} style={{ width: 320 }} />
        <Select
          placeholder="Filter by category" allowClear style={{ width: 240 }}
          value={categoryFilter}
          onChange={(v) => { setCategoryFilter(v); setPage(1) }}
          options={categories.map(c => ({ value: c.code, label: c.name }))}
        />
      </Space>

      <DataTable rowKey="id" columns={columns} dataSource={data?.items} loading={isLoading}
        total={data?.total} page={page} pageSize={pageSize} onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }} />

      <FormDrawer
        title={editing ? `Item: ${editing.item_name}` : 'New Item'}
        open={drawerOpen} onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmit} loading={saveMutation.isPending} form={form}
        width={720}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </FormDrawer>

      {/* Inline "Add Category" modal */}
      <Modal
        title="Add New Item Category"
        open={catModalOpen}
        onCancel={() => setCatModalOpen(false)}
        onOk={() => catForm.validateFields().then(v => addCategoryMutation.mutate(v))}
        confirmLoading={addCategoryMutation.isPending}
      >
        <Form form={catForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="code" label="Code"
            rules={[{ required: true, pattern: /^[A-Z0-9_]+$/, message: 'Uppercase + underscores only' }]}>
            <Input placeholder="e.g. SPARES_HYDRAULIC" style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} />
          </Form.Item>
          <Form.Item name="name" label="Display Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Spares - Hydraulic" />
          </Form.Item>
          <Form.Item name="sub_type" label="Sub-Type" rules={[{ required: true }]}
            initialValue="OTHER"
            help="Controls which fields show on the Item form">
            <Select options={[
              { value: 'RAW_COTTON', label: 'Raw Cotton' },
              { value: 'SPARES',     label: 'Spares' },
              { value: 'CONSUMABLE', label: 'Consumable' },
              { value: 'PACKING',    label: 'Packing' },
              { value: 'CHEMICAL',   label: 'Chemical' },
              { value: 'YARN',       label: 'Yarn (FG)' },
              { value: 'OTHER',      label: 'Other' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
